import { getPref, Prefs, setPref } from '~/common/prefs';
import { customDataLayerId, parseCustomDataCategoryLeafId } from './customDataLayers.helpers';

// Durable on/off state for a project's custom-data layers. Keyed per project because custom-data
// ids are per project, while both of the layer store's own persistence paths are global per user —
// localStorage LAST_USED_LAYERS, or the (organizationId, userId) layer-config document, which
// custom-data ids are barred from entirely.
//
// Pure except for the two localStorage accessors, which read/write inside the call so importing
// this module never touches a browser global (client vitest runs in a `node` environment).

/** Visible leaf ids saved for a project; null when the project has never been recorded. */
export function readCustomDataVisibility(projectId: string): Set<string> | null {
  const raw = getPref(Prefs.CUSTOM_DATA_LAYERS, projectId);
  if (raw === null) return null;
  return new Set(raw.split(',').filter(Boolean));
}

/** Replace a project's record. Sorted so an unchanged selection writes an unchanged string. */
export function writeCustomDataVisibility(projectId: string, ids: Iterable<string>): void {
  setPref(Prefs.CUSTOM_DATA_LAYERS, [...ids].sort().join(','), projectId);
}

/**
 * Whether one leaf should render, given the saved record. A re-ingest rewrites `categories` on the
 * same row id, so a row can move between flat (`customData:<rowId>`) and split (one leaf per
 * CateTitle) without its id changing — inherit across that move rather than dropping to off.
 */
function isLeafOn(leafId: string, saved: ReadonlySet<string>): boolean {
  if (saved.has(leafId)) return true;
  const category = parseCustomDataCategoryLeafId(leafId);
  // Row was flat when saved and is split now: every category inherits the row's state.
  if (category) return saved.has(customDataLayerId(category.rowId));
  // Row was split when saved and is flat now: on if any of its categories was.
  const rowId = leafId.slice(customDataLayerId('').length);
  for (const savedId of saved) {
    if (parseCustomDataCategoryLeafId(savedId)?.rowId === rowId) return true;
  }
  return false;
}

/** Split `leafIds` into the ids that should render and the ids that should not. */
export function resolveCustomDataVisibility(
  saved: ReadonlySet<string> | null,
  leafIds: readonly string[],
): { on: string[]; off: string[] } {
  if (saved === null) return { on: [...leafIds], off: [] };
  const on: string[] = [];
  const off: string[] = [];
  for (const leafId of leafIds) (isLeafOn(leafId, saved) ? on : off).push(leafId);
  return { on, off };
}

export interface CustomDataSyncInput {
  /** Every leaf the TOC currently offers, or undefined while the rows query has no data yet. */
  leafIds: readonly string[] | undefined;
  /** Ids this mount has already decided; the caller owns the set and applies `seed`/`unseed` to it. */
  seededIds: ReadonlySet<string>;
  saved: ReadonlySet<string> | null;
  /** Both custom-data flags resolved ON. False keeps the older default-ON behaviour. */
  rememberEnabled: boolean;
  /** The download form owns map visibility: decide nothing, persist nothing. */
  suppressDefaultOn: boolean;
  /** A download form owns the store: persist nothing. */
  pausePersistence: boolean;
}

export interface CustomDataSyncResult {
  show: string[];
  hide: string[];
  /** Ids to record as decided this mount. */
  seed: string[];
  /** Ids to drop from the render set and from `seededIds` — their row or category is gone. */
  unseed: string[];
  /** Record this project's current selection: it has custom data and no stored record yet. */
  shouldPersist: boolean;
}

/**
 * One reconciliation of the custom-data render set. Extracted whole from the component effect so
 * it stays unit-testable: this repo's component tests render with `renderToStaticMarkup`, which
 * never runs effects.
 */
export function syncCustomDataVisibility(input: CustomDataSyncInput): CustomDataSyncResult {
  const { leafIds, seededIds, saved, rememberEnabled, suppressDefaultOn, pausePersistence } = input;
  const empty: CustomDataSyncResult = { show: [], hide: [], seed: [], unseed: [], shouldPersist: false };
  // `undefined` is "the query has no data", which a background refetch error also produces while
  // cached rows are still in hand. Deciding here would hide every real layer behind an empty record.
  if (leafIds === undefined) return empty;

  const live = new Set(leafIds);
  // Drop ids WE seeded whose row or category is gone (delete / re-ingest / project switch) so they
  // never accumulate in the global layer pref. Only touches seeded ids, never another layer type's.
  const unseed = [...seededIds].filter((id) => !live.has(id));

  if (suppressDefaultOn) return { ...empty, unseed };

  const undecided = leafIds.filter((id) => !seededIds.has(id));
  const { on, off } = resolveCustomDataVisibility(rememberEnabled ? saved : null, undecided);

  // Record the first visit so a later upload is a genuinely new leaf rather than another first
  // visit. Skipped for a project with nothing to record: its first upload is its first visit.
  const shouldPersist = rememberEnabled && !pausePersistence && saved === null && leafIds.length > 0;

  return { show: on, hide: off, seed: undecided, unseed, shouldPersist };
}
